import capitalize from 'lodash/capitalize';
import {Location} from 'react-router';
import min from 'lodash/min';

import {InsightsQueryError, InsightsQueryResults, ShowClause} from 'common/report/insights/util/types';
import {ChartType, FlatSortConfig, InsightsBookmarkParams, SortBy} from 'common/types/reports/bookmark';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {GroupClause} from 'common/report/insights/models/group-clause';
import {getDateRange} from 'common/report/insights/util/time';
import {AdditionalContext, QueryContext, SegmentationMetadata} from 'common/query-context';
import {CurrentProject} from 'common/report/model-resources/projects';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {VIEW_N_DEFAULT_LIMIT} from 'common/components/data-table/constants';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {fetchCustomProperties} from 'common/report/stores/custom-properties';
import {isCustomCsvLimitEnabled} from 'common/widgets/csv-export-modal/utils';
import {promptCsvExportModal} from 'common/widgets/csv-export-modal';
import {getInsightsAggregateCsvExportLimit} from 'common/report/util';
import {hasValidBookmarkParams} from 'common/report/insights/contexts/validation';
import {isMetricLevelDataGroupsEnabled} from 'common/report/util/feature-sampling';

import {ReportManager} from '../report-manager';
import {BookmarkType} from '../queries/app-api/bookmark-client';
import {getReportTracker} from '../report-manager/tracking';
import {
  CSV_EXPORT_CHOICES_PROJECT_OVERRIDES,
  INSIGHTS_AGGREGATE_CSV_EXPORT_CHOICES,
  INSIGHTS_LINE_CSV_EXPORT_LIMIT,
  ProjectFeatureFlag,
  REPORT_LIMITS,
  ReportName,
  SHOW_LAST_X_DAYS_FEATURE_FLAGS,
} from '../constants';
import {DashboardOrigin} from '../util/bookmark-overrides';
import {ProjectDetails} from '../stores/project-details';
import {ClientQueryTrackingProps} from '../tracking-setup';
import {CsvExportAction, CsvExportSpec, QueryContextFnParams} from '../report-manager/types';
import {DisplayBookmark} from '../dashboards/types';
import {IronContextRegistry} from '../context-registry';
import {MetricsResource} from '../model-resources/metrics';
import {
  getQuerySamplingFactor,
  getTimeComparisonTrackingData,
  isEmptyFunnelResult,
  isEmptyResult,
  isEmptyRetentionResult,
} from './util';
import {InsightsQueryBuilderEl} from './query-builder';
import {getReportFromQueryContext, getReportQueryContext} from './query-builder/query-context';
import {InsightsReportHeaderEl} from './report-header';
import {getQueryLimit, InsightsQueryManager} from './query';
import {InsightsVisualizationEl} from './visualization';
import {Report} from './models/report';
import {PRODUCTION_TRACKING_EVENTS} from './tracking';
import {getSelectedSegments} from './util/segments';
import {hasDataTable} from './visualization/charts/chart-display/utils';
import {
  getCustomPropertyReferences,
  hasFunnelMetric,
  hasOneFunnelMetric,
  hasRetentionMetric,
  hasSavedBehavior,
  hasSavedFormula,
  isFunnelShowClause,
  isRetentionOnlyInsightsBookmark,
  isRetentionShowClause,
} from './models/utils';
import {QueryMode} from './util/constants';

export class InsightsReportManager extends ReportManager<
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsQueryError
> {
  public declare insightsReportMode: BookmarkType.Insights | BookmarkType.Funnels | BookmarkType.Retention;

  bookmarkType = BookmarkType.Insights;
  reportDisplayName = `Insights`;

  tracker = getReportTracker({
    appName: {
      [BookmarkType.Insights]: ReportName.Insights,
      [BookmarkType.Funnels]: ReportName.Funnels,
      [BookmarkType.Retention]: ReportName.Retention,
    }[this.insightsReportMode],
    productionEvents: PRODUCTION_TRACKING_EVENTS,
    productionPrefix: `Insights`,
  });

  isCsvExportEnabled = true;

  queryBuilderEl = InsightsQueryBuilderEl;
  reportHeaderEl = InsightsReportHeaderEl;
  visualizationEl = InsightsVisualizationEl;

  queryManager = new InsightsQueryManager({
    projectId: this.projectId,
    workspaceId: this.workspaceId,
    metricsResource: this.metricsResource,
    behaviorsResource: this.behaviorsResource,
  });

  isInsightsAggregateChart(params: {bookmarkParams: InsightsBookmarkParams}): boolean {
    const {bookmarkParams} = params;
    return (
      !hasFunnelMetric(bookmarkParams) &&
      !hasRetentionMetric(bookmarkParams) &&
      [ChartType.Bar, ChartType.Pie, ChartType.Table, ChartType.Metric].includes(
        bookmarkParams.displayOptions.chartType,
      )
    );
  }

  getUxSegmentationLimit(bookmarkParams: InsightsBookmarkParams): number | undefined {
    return getQueryLimit(bookmarkParams);
  }

  getCsvExportLimit(params: {
    bookmarkParams: InsightsBookmarkParams;
    currentProject: CurrentProject;
  }): number | undefined {
    if (this.isInsightsAggregateChart(params)) {
      return isCustomCsvLimitEnabled(params.currentProject)
        ? getInsightsAggregateCsvExportLimit(params.currentProject)
        : REPORT_LIMITS[ReportName.Insights].csvExport;
    }
    const {bookmarkParams} = params;
    const mixedMetricLimits: number[] = [
      ...(hasFunnelMetric(bookmarkParams) ? [REPORT_LIMITS[ReportName.Funnels].csvExport] : []),
      ...(hasRetentionMetric(bookmarkParams) ? [REPORT_LIMITS[ReportName.Retention].csvExport] : []),
      INSIGHTS_LINE_CSV_EXPORT_LIMIT,
    ];
    return min(mixedMetricLimits);
  }

  /**
   * @deprecated use getCsvExportLimit instead.
   * TODO go/j/ae-1194: remove this method when we simplify the logic and move all callers to getCsvExportLimit instead.
   */
  getFixedCsvExportLimit(params: {
    bookmarkParams: InsightsBookmarkParams;
    currentProject: CurrentProject;
  }): number | undefined {
    if (isCustomCsvLimitEnabled(params.currentProject) && this.isInsightsAggregateChart(params)) {
      return undefined;
    }
    return isCustomCsvLimitEnabled(params.currentProject)
      ? INSIGHTS_LINE_CSV_EXPORT_LIMIT
      : REPORT_LIMITS[ReportName.Insights].csvExport;
  }

  async getCsvExportSpec(params: {
    bookmarkParams: InsightsBookmarkParams;
    currentProject: CurrentProject;
  }): Promise<CsvExportSpec> {
    if (isCustomCsvLimitEnabled(params.currentProject) && this.isInsightsAggregateChart(params)) {
      try {
        const {limit} = await promptCsvExportModal({
          limitChoices:
            CSV_EXPORT_CHOICES_PROJECT_OVERRIDES[params.currentProject.id] || INSIGHTS_AGGREGATE_CSV_EXPORT_CHOICES,
        });
        return {
          action: CsvExportAction.Export,
          exportLimit: limit,
          maxCustomLimit: getInsightsAggregateCsvExportLimit(params.currentProject),
        };
      } catch {
        return {action: CsvExportAction.Cancel};
      }
    } else {
      const {bookmarkParams} = params;
      const reportLimits: number[] = [];
      if (
        bookmarkParams.sections.show.some(
          // TODO - go/j/AE-796: Replace these with Pydantic-generated counterparts once fully defined and remove typecasts
          (c) => !isRetentionShowClause(c as ShowClause) && !isFunnelShowClause(c as ShowClause),
        )
      ) {
        reportLimits.push(
          isCustomCsvLimitEnabled(params.currentProject)
            ? INSIGHTS_LINE_CSV_EXPORT_LIMIT
            : REPORT_LIMITS[ReportName.Insights].csvExport,
        );
      }
      if (hasFunnelMetric(bookmarkParams)) {
        reportLimits.push(REPORT_LIMITS[ReportName.Funnels].csvExport);
      }
      if (hasRetentionMetric(bookmarkParams)) {
        reportLimits.push(REPORT_LIMITS[ReportName.Retention].csvExport);
      }
      return {action: CsvExportAction.Export, exportLimit: min(reportLimits)!};
    }
  }

  async csvExportQuery({
    bookmark,
    reportQueryOrigin,
    currentProject,
    useQueryCache = true,
    location,
    limit,
  }: {
    bookmark: DisplayBookmark;
    reportQueryOrigin: BookmarkType;
    currentProject: CurrentProject;
    useQueryCache?: boolean;
    location: Location;
    limit: number;
  }) {
    const {result, filename, isSegmentationLimitHit} = await this.queryManager.csvExportQuery({
      reportQueryOrigin,
      bookmarkParams: bookmark.params,
      currentProject,
      useQueryCache,
      location,
      limit,
    });
    const reportName = bookmark.name || `Untitled Report`;
    const filenameSuffix = filename ? `_${filename}` : `.csv`;
    return {
      result,
      filename: `${reportName}${filenameSuffix}`,
      isSegmentationLimitHit,
    };
  }

  public getAnalysisDescription(params: InsightsBookmarkParams): string {
    return capitalize(params.displayOptions.analysis || undefined);
  }

  public getBookmarkForSaving(params: InsightsBookmarkParams): InsightsBookmarkParams {
    if (!params.sections) {
      return {
        ...params,
        sections: {
          show: [],
          time: [],
        },
      };
    }

    return {
      ...params,
      sections: {
        ...params.sections,
        // remove board filters and breakdowns from the saved bookmark
        filter: params.sections.filter?.filter((filter) => !filter.isInherited),
        group: params.sections.group?.filter((group) => !group.isInherited),
      },
    };
  }

  public hasUnsavedBookmarkParams(original: InsightsBookmarkParams, edited: InsightsBookmarkParams) {
    const before = Report.fromBookmarkData(original).toBookmarkData();
    const after = Report.fromBookmarkData(this.getBookmarkForSaving(edited)).toBookmarkData();

    // for some reason isEqual is not working here
    return JSON.stringify(before) !== JSON.stringify(after);
  }

  public getDefaultBookmarkParams(project: CurrentProject): InsightsBookmarkParams {
    return this.getDefaultReport(project).toBookmarkData();
  }

  public isQueryable(params: InsightsBookmarkParams) {
    return hasValidBookmarkParams(params);
  }

  public isEmptyResults(results: InsightsQueryResults, params: InsightsBookmarkParams) {
    const chartType = params.displayOptions.chartType;
    if (isRetentionOnlyInsightsBookmark(params) && chartType === ChartType.RetentionCurve) {
      return isEmptyRetentionResult(results);
    }
    if (hasOneFunnelMetric(params) && chartType === ChartType.FunnelSteps) {
      return isEmptyFunnelResult(results, params);
    }
    return isEmptyResult(results);
  }

  public getSegmentationMetadatas(results: InsightsQueryResults): SegmentationMetadata[] | undefined {
    const metas = results.meta?.report_sections?.group;
    return metas?.map?.((meta) => ({
      segment: getSelectedSegments([new GroupClause(meta.bookmark)])[0],
      numericBucket: meta.numeric_buckets,
    }));
  }

  public getDateRange(params: InsightsBookmarkParams): DateRange {
    const report = Report.fromBookmarkData(params);
    return getDateRange(report.sections.time.clauses);
  }

  public getBookmarkParamsFromQueryContext(
    context: QueryContext,
    params: QueryContextFnParams,
  ): InsightsBookmarkParams {
    return getReportFromQueryContext(
      context,
      params,
      this.insightsReportMode,
      this.metricsResource,
      this.behaviorsResource,
    ).toBookmarkData();
  }

  public getQueryContextFromBookmarkParams({
    additionalContext,
    behaviorsResource,
    bookmarkType,
    dataGroups,
    metricsResource,
    originDashboard,
    params,
    project,
    querySamplingEnabled,
  }: {
    additionalContext: AdditionalContext;
    behaviorsResource: BehaviorsResource;
    bookmarkType: BookmarkType;
    dataGroups: DataGroup[];
    originDashboard: DashboardOrigin;
    metricsResource: MetricsResource;
    params: InsightsBookmarkParams;
    project: CurrentProject | ProjectDetails;
    querySamplingEnabled: boolean;
  }): QueryContext {
    const report = Report.fromBookmarkData(params);
    report.querySamplingEnabled = querySamplingEnabled;
    return getReportQueryContext(report, {
      defaultReportState: this.getDefaultReport(project),
      originDashboard,
      dataGroups,
      additionalContext,
      bookmarkType,
      currentProject: project as CurrentProject,
      behaviorsResource,
      metricsResource,
      metricType: additionalContext?.metricType,
    });
  }

  public hasDataTable(params: InsightsBookmarkParams): boolean {
    return hasDataTable(params.displayOptions?.chartType);
  }

  public getQuerySamplingFactor(results: InsightsQueryResults): number {
    return getQuerySamplingFactor(results);
  }

  public getClientQueryTrackingProps({
    params,
    project,
    error,
    availableContexts,
  }: {
    params: InsightsBookmarkParams;
    project?: CurrentProject;
    error?: InsightsQueryError;
    availableContexts: Pick<IronContextRegistry, `metrics` | `behaviors` | `i18n`>;
  }): ClientQueryTrackingProps {
    const report = Report.fromBookmarkData(params);
    let timeComparisonTrackingData = null;
    if (report.timeComparison) {
      timeComparisonTrackingData = getTimeComparisonTrackingData({
        timeClause: report.sections.time.clauses[0],
        utcOffset: project?.utcOffset || 0,
        timeComparison: report.timeComparison,
      });
    }

    const extraTrackingProps: Record<string, any> = {};
    const {chartType} = params.displayOptions;
    if (chartType === ChartType.Table) {
      const sortConfig = params.sorting ? params.sorting[chartType] : undefined;
      extraTrackingProps[`Has Modified View N Limit`] =
        (sortConfig?.sortBy === SortBy.Value &&
          `viewNLimit` in sortConfig &&
          Number.isFinite(sortConfig.viewNLimit) &&
          sortConfig.viewNLimit !== VIEW_N_DEFAULT_LIMIT) ||
        (sortConfig?.sortBy === SortBy.Column &&
          sortConfig.colSortAttrs.some(
            (colSortAttr: FlatSortConfig) =>
              Number.isFinite(colSortAttr.viewNLimit) && colSortAttr.viewNLimit !== VIEW_N_DEFAULT_LIMIT,
          ));
    }

    return {
      ...report.toTrackingData({
        behaviorsResource: availableContexts?.behaviors,
        error,
        i18n: availableContexts?.i18n,
        metricsResource: availableContexts?.metrics,
      }),
      ...report.toGroupAnalyticsTrackingData({metricsResource: this.metricsResource}),
      ...report.toLookupTableTrackingData(),
      ...timeComparisonTrackingData,
      'Can Use Multi-Touch Attribution': true,
      ...extraTrackingProps,
    };
  }

  private getDefaultReport(project: CurrentProject | ProjectDetails) {
    const showLastXDaysFlag = SHOW_LAST_X_DAYS_FEATURE_FLAGS.filter((flag: ProjectFeatureFlag) =>
      project?.hasFeatureFlag(flag),
    );

    return Report.createDefaultReport({
      queryMode: project?.isPeopleOnlyProject()
        ? QueryMode.Groups
        : this.insightsReportMode === BookmarkType.Funnels
          ? QueryMode.Funnels
          : this.insightsReportMode === BookmarkType.Retention
            ? QueryMode.Retention
            : QueryMode.Events,
      showLastXDays: showLastXDaysFlag ? showLastXDaysFlag[0] : null,
      defaultToConversionSession: [
        ProjectFeatureFlag.FunnelsMathSessions,
        ProjectFeatureFlag.FunnelsConversionSessions,
      ].some((ff) => project?.hasFeatureFlag(ff)),
      defaultToMathSession: project?.hasFeatureFlag(ProjectFeatureFlag.FunnelsMathSessions),
      metricLevelDataGroups: isMetricLevelDataGroupsEnabled(project),
    });
  }

  public getCommonChartType(params: InsightsBookmarkParams): ChartType {
    return params.displayOptions?.chartType;
  }

  async waitForApiResources({bookmarkParams}: {bookmarkParams: InsightsBookmarkParams}) {
    // Always fetch metrics because we need to know the number of saved formulas before deciding whether to
    // render a menu or an inline entry
    const metricsPromise = this.metricsResource.waitForIndexFetch();
    const behaviorsPromise = this.behaviorsResource.waitForIndexFetch();

    if (hasSavedFormula(bookmarkParams)) {
      // Only block QB render and query fetch if the report contains saved formulas
      await Promise.all([metricsPromise, behaviorsPromise]);
    }

    if (hasSavedBehavior(bookmarkParams)) {
      // Only block QB render and query fetch if the report contains saved behaviors
      await behaviorsPromise;
    }

    if (getCustomPropertyReferences(bookmarkParams).length) {
      await fetchCustomProperties();
    }
  }
}
